/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import * as echarts from "echarts";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import { ColorLib } from "../../utils/color-lib";
import { isEqual } from "lodash";
export class DiscoveryCalendar {
    constructor() {
        this.defOptions = new Param();
        this.divider = 1000;
        this.CAL_SIZE = 150;
        this.result = undefined;
        this.type = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = undefined;
        this.parsing = false;
        this.rendering = false;
        this.innerOptions = undefined;
    }
    updateType(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.chartOpts = this.convert(GTSLib.getData(this.result));
            setTimeout(() => {
                this.myChart.setOption(this.chartOpts || {}, true, false);
                this.myChart.resize({ height: this.height });
                this.setOpts(true);
            });
        }
    }
    updateRes() {
        this.chartOpts = this.convert(GTSLib.getData(this.result));
        setTimeout(() => {
            this.myChart.setOption(this.chartOpts || {}, true, false);
            this.myChart.resize({ height: this.height });
            this.setOpts(true);
        });
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            if (!!this.myChart) {
                this.chartOpts = this.convert(this.result || new DataModel());
                setTimeout(() => {
                    this.myChart.setOption(this.chartOpts || {}, true, false);
                    this.myChart.resize({ height: this.height });
                    this.setOpts(true);
                });
            }
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue }, this.chartOpts);
        }
    }
    async resize() {
        if (this.myChart) {
            this.myChart.resize();
        }
        return Promise.resolve();
    }
    async show(regexp) {
        this.myChart.dispatchAction({
            type: 'legendSelect',
            batch: this.myChart.getOption().series.filter(s => new RegExp(regexp).test(s.name)),
        });
        return Promise.resolve();
    }
    async hide(regexp) {
        this.myChart.dispatchAction({
            type: 'legendUnSelect',
            batch: this.myChart.getOption().series.filter(s => new RegExp(regexp).test(s.name)),
        });
        return Promise.resolve();
    }
    async hideById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendUnSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    async showById(id) {
        if (this.myChart) {
            this.myChart.dispatchAction({
                type: 'legendSelect',
                batch: this.myChart.getOption().series
                    .filter((s, i) => new RegExp(id.toString()).test((s.id || i).toString())),
            });
        }
        return Promise.resolve();
    }
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryCalendar, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.innerOptions.timeUnit || 'us');
        this.chartOpts = this.convert(this.result || new DataModel());
        this.setOpts();
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
            chartOpts: this.chartOpts,
        });
    }
    convert(data) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        options = Utils.mergeDeep(options || {}, data.globalParams);
        this.innerOptions = Object.assign({}, options);
        const series = [];
        const calendar = [];
        const titles = [];
        const visualMap = [];
        let gtsList;
        if (GTSLib.isArray(data.data)) {
            data.data = GTSLib.flatDeep(data.data);
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert', 'isArray']);
            if (data.data.length > 0 && GTSLib.isGts(data.data[0])) {
                (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['convert', 'isArray 2']);
                gtsList = GTSLib.flattenGtsIdArray(data.data, 0).res;
            }
            else {
                (_c = this.LOG) === null || _c === void 0 ? void 0 : _c.debug(['convert', 'isArray 3']);
                gtsList = data.data;
            }
        }
        else {
            (_d = this.LOG) === null || _d === void 0 ? void 0 : _d.debug(['convert', 'not array']);
            gtsList = [data.data];
        }
        (_e = this.LOG) === null || _e === void 0 ? void 0 : _e.debug(['convert'], { options: this.innerOptions, gtsList });
        const gtsCount = gtsList.length;
        let cal = 0;
        let seriesIndex = 0;
        for (let i = 0; i < gtsCount; i++) {
            const datasetNoAlpha = (_h = (_g = ((_f = data.params) !== null && _f !== void 0 ? _f : [])[i]) === null || _g === void 0 ? void 0 : _g.datasetNoAlpha) !== null && _h !== void 0 ? _h : this.innerOptions.datasetNoAlpha;
            const gts = gtsList[i];
            let min = Number.MAX_SAFE_INTEGER;
            let max = Number.MIN_SAFE_INTEGER;
            const dataStruct = {};
            if (GTSLib.isGtsToPlot(gts) && !!gts.v) {
                // add title
                titles.push({
                    text: ((data.params || [])[i] || { key: undefined }).key || GTSLib.serializeGtsMetadata(gts),
                    left: 'center',
                    textStyle: {
                        height: 20, fontSize: 12,
                        color: Utils.getLabelColor(this.el),
                    },
                    top: this.CAL_SIZE * cal + seriesIndex * 20,
                });
                // Find min/max
                (gts.v || []).forEach((v) => {
                    const value = v[v.length - 1];
                    const d = GTSLib.toISOString(v[0], this.divider, this.innerOptions.timeZone, undefined);
                    const y = d.split('-')[0];
                    dataStruct[y] = dataStruct[y] || {};
                    // Aggregation
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    dataStruct[y][d] = dataStruct[y][d] + value || value;
                    min = Math.min(min, dataStruct[y][d]);
                    max = Math.max(max, dataStruct[y][d]);
                });
                Object.keys(dataStruct).forEach(currentRange => {
                    var _a, _b, _c;
                    // Add VisualMap and Calendar
                    visualMap.push({
                        min, max, show: false,
                        seriesIndex: cal,
                        color: ColorLib.getHeatMap(((data.params || [])[i] || {}).scheme || this.innerOptions.scheme),
                    });
                    calendar.push({
                        top: this.CAL_SIZE * cal + (seriesIndex + 1) * 20 + 20,
                        range: currentRange,
                        cellSize: ['auto', 15],
                        itemStyle: {
                            color: 'transparent',
                            borderWidth: 1,
                            borderColor: ColorLib.transparentize(Utils.getGridColor(this.el), datasetNoAlpha ? 1 : 0.5),
                        },
                        splitLine: { lineStyle: { width: 2, color: Utils.getGridColor(this.el) } },
                        dayLabel: {
                            firstDay: ((_a = this.innerOptions.calendar) === null || _a === void 0 ? void 0 : _a.firstDay) || 0,
                            nameMap: (_b = this.innerOptions.calendar) === null || _b === void 0 ? void 0 : _b.dayLabel,
                            color: Utils.getLabelColor(this.el),
                        },
                        monthLabel: {
                            nameMap: (_c = this.innerOptions.calendar) === null || _c === void 0 ? void 0 : _c.monthLabel,
                            color: Utils.getLabelColor(this.el),
                        },
                        yearLabel: { color: Utils.getLabelColor(this.el) },
                    });
                    series.push({
                        type: 'heatmap',
                        coordinateSystem: 'calendar',
                        name: ((data.params || [])[i] || { key: undefined }).key || GTSLib.serializeGtsMetadata(gts),
                        calendarIndex: cal,
                        data: Object.keys(dataStruct[currentRange]).map(d => [d, dataStruct[currentRange][d]]),
                    });
                    cal++;
                });
                seriesIndex++;
            }
        }
        this.height = this.CAL_SIZE * cal + titles.length * 20 + 40;
        (_j = this.LOG) === null || _j === void 0 ? void 0 : _j.debug(['convert', 'series'], { series, calendar, visualMap, titles });
        return Object.assign({ title: titles, grid: {
                left: 10, top: 10, bottom: 10, right: 10,
                containLabel: true,
            }, tooltip: {
                trigger: 'item',
                axisPointer: {
                    type: 'shadow',
                },
                backgroundColor: Utils.getCSSColor(this.el, '--warp-view-tooltip-bg-color', 'white'),
                hideDelay: this.innerOptions.tooltipDelay || 100,
                formatter: (params) => {
                    return `<div style="font-size:14px;color:#666;font-weight:400;line-height:1;">${GTSLib.toISOString(GTSLib.toTimestamp(params.value[0], this.divider, this.innerOptions.timeZone), this.divider, this.innerOptions.timeZone, this.innerOptions.fullDateDisplay ? this.innerOptions.timeFormat : undefined).replace('T', ' ').replace('Z', '')}</div>
            ${params.marker}
            <span style="font-size:14px;color:#666;font-weight:400;margin-left:2px">${params.seriesName}</span>
            <span style="float:right;margin-left:20px;font-size:14px;color:#666;font-weight:900">
            ${params.value[1]}</span>`;
                },
            }, toolbox: {
                show: this.innerOptions.showControls,
                feature: {
                    saveAsImage: { type: 'png', excludeComponents: ['toolbox'] },
                },
            }, legend: { bottom: 10, left: 'center', show: false }, visualMap,
            series,
            calendar }, ((_l = (_k = this.innerOptions) === null || _k === void 0 ? void 0 : _k.extra) === null || _l === void 0 ? void 0 : _l.chartOpts) || {});
    }
    async export(type = 'png') {
        return Promise.resolve(this.myChart ? this.myChart.getDataURL({
            type,
            excludeComponents: ['toolbox'],
        }) : undefined);
    }
    setOpts(notMerge = false) {
        var _a;
        if ((((_a = this.chartOpts) === null || _a === void 0 ? void 0 : _a.series) || []).length === 0) {
            this.chartOpts.title = {
                show: true,
                textStyle: { color: Utils.getLabelColor(this.el), fontSize: 20 },
                text: this.innerOptions.noDataLabel || '',
                left: 'center',
                top: 'center',
            };
            this.chartOpts.xAxis = { show: false };
            this.chartOpts.yAxis = { show: false };
            this.chartOpts.tooltip = { show: false };
        }
        else {
            this.chartOpts.title = Object.assign(Object.assign({}, this.chartOpts.title || {}), { show: false });
        }
        setTimeout(() => {
            if (this.myChart) {
                this.myChart.setOption(this.chartOpts || {}, notMerge, true);
            }
        });
    }
    componentDidLoad() {
        setTimeout(() => {
            this.parsing = false;
            this.rendering = true;
            let initial = false;
            this.myChart = echarts.init(this.graph, null, {
                width: this.width,
                height: this.height ? this.height - 10 : undefined,
            });
            this.myChart.on('rendered', () => {
                this.rendering = false;
                if (initial) {
                    setTimeout(() => this.draw.emit());
                    initial = false;
                }
            });
            this.myChart.on('mouseover', (event) => {
                this.dataPointOver.emit({ date: event.value[0], name: event.seriesName, value: event.value[1], meta: {} });
            });
            this.el.addEventListener('mouseout', () => this.dataPointOver.emit({}));
            this.myChart.on('click', (event) => {
                this.dataPointSelected.emit({ date: event.value[0], name: event.seriesName, value: event.value[1], meta: {} });
            });
            this.myChart.setOption(this.chartOpts || {}, true, false);
            initial = true;
        });
    }
    render() {
        return h("div", { key: '67bb2fa2253d19ed56654815e08cee42a6b59a90', class: "calendar-wrapper" }, this.parsing ? h("discovery-spinner", null, "Parsing data...") : '', this.rendering ? h("discovery-spinner", null, "Rendering data...") : '', h("div", { key: 'a750840f092023bcd976fe65d2f59ce8f981e888', ref: (el) => this.graph = el }));
    }
    static get is() { return "discovery-calendar"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-calendar.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-calendar.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "parsing": {},
            "rendering": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "show": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "(regexp: string) => Promise<void>",
                    "parameters": [{
                            "name": "regexp",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "hideById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "showById": {
                "complexType": {
                    "signature": "(id: number | string) => Promise<void>",
                    "parameters": [{
                            "name": "id",
                            "type": "string | number",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "type",
                "methodName": "updateType"
            }, {
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
}
//# sourceMappingURL=discovery-calendar.js.map
